import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { CoverBars } from "../components/CoverBars";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { Notice } from "../components/Notice";
import { ProgressBar } from "../components/ProgressBar";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import {
  coverBudgetInsight,
  coverCannotSee,
  coverInsight,
  coverLimitsLine,
  coverNames,
  windowCover,
} from "../domain/cover";
import { cycleAffected, cycleOn, prePeriodDay } from "../domain/cycle";
import { addDays, parseDay, todayStr } from "../domain/date";
import {
  MEAL_PROTEIN_AIM_G,
  foodInsights,
  intakeByPhase,
  proteinByMeal,
  proteinDaysInsight,
  topSources,
  weakestHeading,
  type FoodDeps,
  type NutrientKey,
} from "../domain/foodTrends";
import { foodWeek, weekCountLine, weekOf } from "../domain/foodWeek";
import { WINDOW_LABEL, type Insight as InsightData, type Window } from "../domain/insight";
import { dayIntake, entriesFor, foodOn } from "../domain/intake";
import { dayLedger } from "../domain/ledger";
import { dailyTargets } from "../domain/nutrition";
import { energyView, signed } from "../domain/progressView";
import type { MealSlot } from "../domain/types";
import { waterInsight } from "../domain/water";
import { useGo } from "../lib/useGo";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import "./Progress.css";

const WINDOWS: Window[] = [28, 90, 0];
/** The top of a meal's aim; the bars tick at the bottom (25 g) and the words give the range. */
const MEAL_AIM_TOP_G = 30;

/** Evening, not "Snacks": the slot is the fourth meal of the day, and the artboard names it so. */
const MEAL_ROWS: { slot: MealSlot; label: string }[] = [
  { slot: "breakfast", label: "Breakfast" },
  { slot: "lunch", label: "Lunch" },
  { slot: "snack", label: "Evening" },
  { slot: "dinner", label: "Dinner" },
];

const SOURCE_ROWS: { key: NutrientKey; label: string }[] = [
  { key: "p", label: "Protein" },
  { key: "fb", label: "Fibre" },
  { key: "fe", label: "Iron" },
];

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
/** grams to a tenth under 10, whole above */
const grams = (n: number) => (n < 10 ? (Math.round(n * 10) / 10).toLocaleString("en-US") : fmt(n));
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "Moong dal (split), cooked" → "moong dal": a list of three reads as foods, not labels. */
function shortName(name: string): string {
  const s = name.split(/[,(/]/)[0].trim();
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * /progress/food — food over a window that ends yesterday (a half-logged today drags every
 * average). The answer is the nutrient cover (ADR-040): how much of what five nutrients need the
 * logged days carried, with each nutrient's bar and the limits beside it; then coverage and
 * budget, protein by meal, the weakest nutrient, the foods that carry them, and the patterns.
 * Food (the tab) owns today and the plan; this page owns the trends and links there for today.
 * Food off: one Notice and the links.
 */
export default function ProgressFood() {
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  const go = useGo();
  const [w, setW] = useState<Window>(28);

  const today = todayStr();
  const end = addDays(today, -1);
  const food = foodOn(S);
  const cyc = cycleOn(S);
  const win = WINDOW_LABEL[w];

  const ev = energyView(S, today, w, (ds) => cycleAffected(S, ds));
  const phase = cyc ? intakeByPhase(S, end, 0, (ds) => prePeriodDay(S, ds)) : null;

  const connected = (
    <RowGroup heading="Connected">
      <NavRow
        title="Energy"
        icon={I.flame}
        subtitle={food && ev.deficit !== null ? `${signed(ev.deficit)} kcal a day est.` : "In vs Out, and what the scale says"}
        to="/progress/energy"
      />
      {cyc ? (
        <NavRow
          title="Cycle"
          icon={I.drop}
          tone="cycle"
          subtitle={
            phase && phase.diff !== null
              ? `Appetite before a period · ${signed(Math.round(phase.diff / 10) * 10)} kcal`
              : "Appetite before a period"
          }
          to="/progress/cycle"
        />
      ) : null}
    </RowGroup>
  );

  if (!food) {
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title="Food" back={{ to: "/progress", label: "Back to Progress" }} />
            <div className="pg">
              <Notice secondary={{ label: "Food settings", onClick: () => go("/you/food") }}>
                Food logging is off, so there are no food trends to show.
              </Notice>
              {connected}
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );
  }

  const budget = (ds: string) => dayLedger(S, ds, parseDay(ds), device).budget;
  const deps: FoodDeps = { budget, prePeriod: (ds) => prePeriodDay(S, ds), cycleOn: cyc };
  const all = foodInsights(S, end, w, deps);
  const pick = (...keys: string[]): InsightData[] =>
    keys.map((k) => all.find((i) => i.key === k)).filter((i): i is InsightData => i !== undefined);

  const week = foodWeek(S, weekOf(end), end, budget);
  const wc = windowCover(S, end, w, budget);
  const coverRow = coverInsight(wc, w);
  const budgetRow = coverBudgetInsight(wc);
  const limits = wc.ready ? coverLimitsLine(S, end, w) : null;

  const pm = proteinByMeal(S, end, w);
  const proteinRow = proteinDaysInsight(S, end, w);
  const T = dailyTargets(S, parseDay(end), undefined, device);
  const needsBody = T.kcal === null || T.fibre === null || T.iron === null;
  const mealMax = Math.max(40, ...MEAL_ROWS.map((m) => pm.bySlot[m.slot] ?? 0));

  const todayIn = entriesFor(S, today).length ? dayIntake(S, today).kcal : null;
  const weakest = pick("food.weakest")[0];
  const protein = pick("food.protein")[0];

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Food" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              label={`Nutrient cover · ${win}`}
              figure={wc.ready && wc.cover !== null ? Math.round(wc.cover) : null}
              unit="% nutrient cover"
              line={
                wc.ready
                  ? `of what ${coverNames(wc)} need, on ${wc.days} logged ${plural(wc.days, "day")}`
                  : wc.noBudget
                    ? "needs your body numbers, which set the targets"
                    : coverRow.wait
                      ? `appears ${coverRow.wait}`
                      : "no food logged in this window"
              }
              visual={
                <div className="pg-hero__switch">
                  <Segmented<Window>
                    options={WINDOWS.map((x) => ({ value: x, label: WINDOW_LABEL[x] }))}
                    value={w}
                    onChange={setW}
                    ariaLabel="Window"
                  />
                </div>
              }
            />

            {needsBody ? (
              <Notice secondary={{ label: "Body numbers", onClick: () => go("/you/body") }}>
                Budget, fibre and iron targets need your year of birth and sex, in You › Body.
              </Notice>
            ) : null}

            <RowGroup heading="Five nutrients · a day">
              <Insight insight={coverRow} first />
              {wc.ready ? (
                <div className="pg-block">
                  <CoverBars
                    caption="Share of each need covered · the full bar is the need"
                    rows={wc.bars.map((b) => ({
                      key: b.key,
                      label: b.label,
                      unit: b.unit,
                      share: b.share,
                      amount: b.avg,
                      target: b.target,
                    }))}
                  />
                  {limits ? <p className="pg-muted">{limits}</p> : null}
                  <p className="pg-muted">{coverCannotSee(S, parseDay(end))}</p>
                </div>
              ) : null}
            </RowGroup>

            <RowGroup heading="Coverage and budget">
              {[...pick("food.coverage", "food.adherence"), ...(budgetRow ? [budgetRow] : [])].map((i, n) => (
                <Insight key={i.key} insight={i} first={n === 0} />
              ))}
              {/* one line for water, always the last seven days: a count, never a streak (ADR-044) */}
              <Insight
                insight={waterInsight(S, today)}
                first={!pick("food.coverage", "food.adherence").length}
              />
              {/* the week on complete days has its own page (ProgressFoodWeek, ADR-049): a row value here */}
              <NavRow
                icon={I.calendar}
                title="Food by week"
                subtitle={`This week · ${weekCountLine(week)}`}
                value={week.avgKcal === null ? "–" : `${fmt(week.avgKcal)} kcal`}
                to="/progress/food/week"
              />
            </RowGroup>

            <RowGroup heading={`Protein by meal · aim ${MEAL_PROTEIN_AIM_G}–${MEAL_AIM_TOP_G} g`}>
              {protein?.wait ? null : (
                <div className="pg-block pg-bars">
                  {MEAL_ROWS.map(({ slot, label }) => {
                    const g = pm.bySlot[slot];
                    const shown = g === null ? "–" : grams(g);
                    return (
                      <ProgressBar
                        key={slot}
                        value={g}
                        max={mealMax}
                        target={MEAL_PROTEIN_AIM_G}
                        label={label}
                        detail={`${shown} of ${MEAL_PROTEIN_AIM_G}–${MEAL_AIM_TOP_G} g`}
                        ariaLabel={`${label}: ${g === null ? "not eaten in this window" : `${shown} g`} of an aim of ${MEAL_PROTEIN_AIM_G} to ${MEAL_AIM_TOP_G} g`}
                      />
                    );
                  })}
                </div>
              )}
              {protein ? <Insight insight={protein} first={!!protein.wait} /> : null}
              {proteinRow ? <Insight insight={proteinRow} /> : null}
            </RowGroup>

            {weakest ? (
              <RowGroup heading={`${weakestHeading(S, end, w, deps)} · ${win}`}>
                <Insight insight={weakest} first />
              </RowGroup>
            ) : null}

            <RowGroup heading={`Top sources · ${win}`}>
              {SOURCE_ROWS.map(({ key, label }) => {
                const names = [...new Set(topSources(S, end, w, key).map((s) => shortName(s.name)))];
                return (
                  <NavRow
                    key={key}
                    kind="value"
                    title={label}
                    value={names.length ? <span>{names.join(", ")}</span> : "–"}
                  />
                );
              })}
            </RowGroup>

            <RowGroup heading="Patterns">
              {pick("food.weekend", "food.cycle").map((i, n) => (
                <Insight key={i.key} insight={i} first={n === 0} />
              ))}
            </RowGroup>

            <RowGroup>
              <NavRow
                title="Today’s food"
                icon={I.bowl}
                subtitle={todayIn === null ? "Nothing logged yet · Food tab" : `${fmt(todayIn)} kcal in · Food tab`}
                to="/food"
              />
            </RowGroup>

            {connected}
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
